/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import React from "react";
import { Card, FormControlLabel, FormGroup, IconButton, Switch } from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import CloseIcon from "@material-ui/icons/Close";
import { ImageCropDialog } from "../../../components/ImageCropDialog/ImageCropDialog";
import { ChatHeaderDemoDisplay } from "./WebChatPreviewComponents/HeaderDemoDisplay";

export const HeaderCropDialog = ({ selectedConfiguration, setConfigValue, setCropModalOpen, cropModalOpen }) => {
	const [headerDimensions, setHeaderDimensions] = React.useState({
		height: 0,
		width: 0,
	});
	const [cropExpanded, setCropExpanded] = React.useState(false);
	const [submit, setSubmit] = React.useState(false);
	const [controlPanelOpen, setControlPanelOpen] = React.useState(true);
	const [zoomControl, setZoomControl] = React.useState(undefined);
	const [isMuted, setIsMuted] = React.useState(false);
	const [showHeaderPreview, setShowHeaderPreview] = React.useState(true);
	const [originalHeaderSize, setOriginalHeaderSize] = React.useState(undefined);
	const onCropComplete = React.useCallback(
		(croppedArea, croppedAreaPercentage) => {
			if (submit || !cropExpanded) {
				setConfigValue("headerImageCropSettings", { croppedArea, croppedAreaPercentage, headerDimensions });
				setCropModalOpen(false);
			} else {
				setSubmit(true);
			}
		},
		[setConfigValue, headerDimensions, submit, setCropModalOpen, cropExpanded],
	);

	const refFunc = (node) => {
		if (node && originalHeaderSize && headerDimensions.height !== node.clientHeight) {
			let dif = 0;
			dif = Math.abs(originalHeaderSize - node.clientHeight) / 2;
			dif = originalHeaderSize < node.clientHeight ? dif : -dif;
			setHeaderDimensions({
				...headerDimensions,
				width: submit || !cropExpanded ? 370 : 550,
				height: node.clientHeight,
				transformY: dif,
			});
		} else if (node && !originalHeaderSize) {
			setOriginalHeaderSize(node.clientHeight);
		}
	};

	return (
		<ImageCropDialog
			imageUrl={selectedConfiguration.headerBackgroundImage}
			onClose={() => setCropModalOpen(false)}
			open={cropModalOpen}
			dialogArguments={{ maxWidth: "lg" }}
			onCropComplete={onCropComplete}
			title={"Header Background Image"}
			submitButtonText={"Confirm Changes"}
			subtitle={
				"This preview is an estimation. The exact area of the image shown in the chat widget header may vary slightly from what is shown below."
			}
			getZoomControl={setZoomControl}
			cropperStyles={{
				containerStyle: { backgroundColor: selectedConfiguration.headerBackgroundColor || "#941012" },
				cropAreaStyle: {
					borderRadius: "5px 5px 0px 0px",
					//Overriding the default settings for cropper area. Currently it gets translated down and right 50% just to come back up and left 50%.
					//This override negates that and allows us to put our own tranlate on the element to compensate for vertical movement of the dynamic height.
					left: "0px",
					top: "0px",
					transform: `translate(0px, ${headerDimensions.transformY || 0}px`,
					position: "relative",
				},
			}}
			initialZoom={selectedConfiguration.headerImageCropSettings?.croppedArea?.zoom || 1}
			cropSize={{
				...headerDimensions,
				x: selectedConfiguration.headerImageCropSettings?.croppedArea?.x || 0,
				y: selectedConfiguration.headerImageCropSettings?.croppedArea?.y || 0,
			}}
			submitPerRender={submit}
			intitialSettings={selectedConfiguration.headerImageCropSettings?.croppedAreaPercentage}
			content={
				<>
					<div
						style={{
							position: "absolute",
							right: "10px",
							bottom: "10px",
							zIndex: 10,
						}}
					>
						{controlPanelOpen ? (
							<Card style={{ padding: "10px" }}>
								<div>
									<div style={{ display: "flex", justifyContent: "space-between" }}>
										<FormGroup style={{ margin: "5px 5px 5px 0px" }}>
											<FormControlLabel
												labelPlacement="top"
												control={
													<Switch
														onChange={(event) => {
															setHeaderDimensions({
																...headerDimensions,
																width: event.target.checked ? 550 : 370,
															});
															setCropExpanded(event.target.checked);
														}}
														checked={cropExpanded}
													/>
												}
												style={{ margin: "0px" }}
												label={<div style={{ fontSize: "12px", color: "#0000008A" }}>Expand</div>}
											/>
											<FormControlLabel
												labelPlacement="top"
												control={
													<Switch
														onChange={(event) => {
															setIsMuted(event.target.checked);
														}}
														checked={isMuted}
														disabled={!showHeaderPreview || !selectedConfiguration.enableAudioNotification}
													/>
												}
												style={{ margin: "0px" }}
												label={<div style={{ fontSize: "12px", color: "#0000008A" }}>Mute</div>}
											/>
										</FormGroup>
										<FormGroup style={{ margin: "5px" }}>
											<FormControlLabel
												labelPlacement="top"
												control={
													<Switch
														onChange={(event) => {
															setShowHeaderPreview(event.target.checked);
														}}
														checked={showHeaderPreview}
													/>
												}
												style={{ margin: "0px" }}
												label={<div style={{ fontSize: "12px", color: "#0000008A" }}>Header Elements</div>}
											/>
										</FormGroup>
										<div>
											<IconButton size="small" style={{ margin: "0px" }} onClick={() => setControlPanelOpen(false)}>
												<CloseIcon />
											</IconButton>
										</div>
									</div>
									<div style={{ margin: "0px 15px" }}>{zoomControl && zoomControl}</div>
								</div>
							</Card>
						) : (
							<IconButton size="medium" style={{ color: "white" }} onClick={() => setControlPanelOpen(true)}>
								<SettingsIcon fontSize="large" />
							</IconButton>
						)}
					</div>
					<div
						style={{
							position: "absolute",
							zIndex: 1,
							height: "100%",
							width: "100%",
							display: "flex",
							justifyContent: "center",
							pointerEvents: "none",
							margin: "auto",
						}}
					>
						<div
							ref={refFunc}
							style={{
								width: !submit && cropExpanded ? 550 : 370,
								minHeight: "90px",
								margin: "auto",
								overflow: "none",
								opacity: showHeaderPreview ? 1 : 0,
								//This transform allows us to compensate for the different heights that might occur
								//between expanded and none expanded headers due to title length
								transform: `translate(0px, ${headerDimensions.transformY || 0}px`,
								position: "relative",
							}}
						>
							<div>
								<ChatHeaderDemoDisplay
									isChatWidgetMaximized={!submit && cropExpanded}
									title={selectedConfiguration.title}
									isMuted={isMuted}
									selectedConfig={selectedConfiguration}
									transparentBackground
								/>
							</div>
						</div>
					</div>
				</>
			}
		/>
	);
};
